import type { StructuredToolInterface } from '@langchain/core/tools';
import { Scratchpad } from './scratchpad.js';
import { TokenCounter } from './token-counter.js';
import type { CreditUsage } from '../tools/finance/credit-usage.js';

interface RunCreditUsage {
  credits: number;
  calls: number;
  unknownCalls: number;
}

/**
 * Mutable state for a single agent run.
 */
export interface RunContext {
  readonly query: string;
  readonly scratchpad: Scratchpad;
  readonly tokenCounter: TokenCounter;
  readonly startTime: number;
  iteration: number;
  /**
   * Input token count from the most recent API response.
   * This is the actual context size reported by the API — far more accurate
   * than character-based estimation. Used by manageContextThreshold() to
   * anchor token estimates on real data.
   */
  lastApiInputTokens: number;
  /**
   * MCP tools activated on demand by the `search_tools` tool during this run.
   * The agent loop merges these into the tool set bound to the LLM on the next
   * iteration, so only discovered MCP tools enter the context (never all of them).
   */
  activeMcpTools: StructuredToolInterface[];
  /** Optional account-health seed text surfaced by the preflight, if any. */
  accountSeed?: string;
  /** Drishti API/MCP credit usage accumulated for this one query. */
  creditUsage: RunCreditUsage;
  /**
   * Set when a tool reports a billing/credit exhaustion error. The agent should
   * stop spending tool credits for this turn and answer from available context.
   */
  creditExhausted?: {
    tool: string;
    error: string;
  };
}

export function createRunContext(query: string): RunContext {
  return {
    query,
    scratchpad: new Scratchpad(query),
    tokenCounter: new TokenCounter(),
    startTime: Date.now(),
    iteration: 0,
    lastApiInputTokens: 0,
    activeMcpTools: [],
    creditUsage: { credits: 0, calls: 0, unknownCalls: 0 },
  };
}

export function addRunCreditUsage(ctx: RunContext, usage?: CreditUsage): void {
  if (!usage) return;

  ctx.creditUsage.calls += usage.calls;
  ctx.creditUsage.unknownCalls += usage.unknownCalls;
  if (typeof usage.credits === 'number') {
    ctx.creditUsage.credits += usage.credits;
  }
}

export function getRunCreditUsage(ctx: RunContext): CreditUsage | undefined {
  const usage = ctx.creditUsage;
  if (usage.calls === 0) return undefined;

  return {
    ...(usage.calls > usage.unknownCalls ? { credits: usage.credits } : {}),
    calls: usage.calls,
    unknownCalls: usage.unknownCalls,
    // Keep session-shaped fields local to this query for legacy renderers.
    sessionCredits: usage.credits,
    sessionCalls: usage.calls,
    sessionUnknownCalls: usage.unknownCalls,
  };
}

export function markCreditExhausted(ctx: RunContext, tool: string, error: string): void {
  ctx.creditExhausted ??= { tool, error };
}
